import React from 'react';
import { Button, Glyphicon, Tab, Tabs } from 'react-bootstrap';
import { translate } from 'react-i18next';
import { RectangularInputForm } from './RectangularInputForm';
import { InputFormCard } from './InputFormCard';

/**
 * - Show/hide between tab
 * - Add, remove window form and shading device forms
 */
export class SCControlPanel extends React.Component {
  static convertToDevConfig(dev) {
    return {
      origin: {
        x: dev.x,
        y: dev.y,
        z: dev.z,
      },
      size: {
        width: dev.width,
        height: dev.height || dev.length,
      },
      xAngle: dev.angle,
    };
  }

  static getConfigForDevice(type, windowWidth, windowHeight) {
    const defaultDeviceConfig = {
      origin: {
        x: 0,
        y: 0,
        z: 0,
      },
      size: {
        width: windowWidth,
        height: windowHeight,
      },
      isShow: true,
      isHighLight: false,
    };

    switch (type) {
      case 'right':
        return {
          ...defaultDeviceConfig,
          origin: {
            x: 0,
            y: windowWidth,
            z: 0,
          },
        };
      case 'top':
        return {
          ...defaultDeviceConfig,
          origin: {
            x: 0,
            y: 0,
            z: windowHeight,
          },
          xAngle: 75,
        };
      case 'front':
        return {
          ...defaultDeviceConfig,
          origin: {
            x: windowHeight,
            y: 0,
            z: windowHeight,
          },
          xAngle: 10,
        };
      default:
        return defaultDeviceConfig;
    }
  }

  constructor(props) {
    super(props);

    this.state = {
      windowWidth: props.window.width,
      windowHeight: props.window.height,
      leftDevices: props.shadingDevices.left || [],
      rightDevices: props.shadingDevices.right || [],
      topDevices: props.shadingDevices.top || [],
      frontDevices: props.shadingDevices.front || [],
    };

    this.creationCount = [
      ...this.state.leftDevices, ...this.state.rightDevices, ...this.state.topDevices, ...this.state.frontDevices
    ]
      .map(dev => parseInt(dev.name.split(' ')[1], 10))
      .reduce((maxId, id) => Math.max(maxId, id), 0);
  }

  addDevice(deviceType, stateKey) {
    this.creationCount++;
    const addedDev = {
      ...SCControlPanel.getConfigForDevice(deviceType, this.state.windowWidth, this.state.windowHeight),
      name: `Device ${this.creationCount}`,
    };

    this.setState(
      {
        [stateKey]: [addedDev, ...this.state[stateKey]],
      },
      () => this.notifyDataChanged(),
    );
  }

  removeDevice(stateKey, removeIndex) {
    this.setState(
      {
        [stateKey]: this.state[stateKey].filter((item, index) => index !== removeIndex)
      },
      () => this.notifyDataChanged(),
    );
  }

  showHideDevice(stateKey, targetIndex, isShow) {
    this.setState(
      {
        [stateKey]: this.state[stateKey].map((item, index) => index === targetIndex ? ({ ...item, isShow }) : item)
      },
      () => this.notifyDataChanged(),
    );
  }

  changeDevice(stateKey, name, data) {
    const newList = this.state[stateKey].map(
      item => item.name === name ? ({ ...item, ...SCControlPanel.convertToDevConfig(data) }) : item
    );
    this.setState(
      {
        [stateKey]: newList,
      },
      () => this.notifyDataChanged(),
    );
  }

  focusDevice(name) {
    const updatedDevices = Object.keys(this.state)
      .filter(key => key.toLowerCase().indexOf('devices') !== -1)
      .reduce((acc, deviceType) => {
        const newList = this.state[deviceType].map(device => ({ ...device, isHighLight: device.name === name}));
        return { ...acc, [deviceType]: newList };
      }, {});

    this.setState(updatedDevices, () => this.notifyDataChanged());
  }

  changeWindow({ width, height }) {
    this.setState(
      {
        windowWidth: width,
        windowHeight: height,
      },
      () => this.notifyDataChanged(),
    );
  }

  notifyDataChanged() {
    const { onDataChanged } = this.props;
    if (onDataChanged) {
      const renderData = Object.keys(this.state)
        .filter(key => key.toLowerCase().indexOf('devices') !== -1)
        .reduce(
          (devices, key) => {
            const filteredDevices = devices[key].filter(device => device.isShow);
            return { ...devices, [key]: filteredDevices };
          },
          { ...this.state },
        );
      onDataChanged(renderData);
    }
  }

  componentDidMount() {
    this.notifyDataChanged();
  }

  renderWindow() {
    const { window, t } = this.props;
    return (
      <InputFormCard title={'window'} t={t}>
        <RectangularInputForm
          name="window"
          size={window}
          onDataChanged={(name, data) => this.changeWindow(data)}
          t={t}
        />
      </InputFormCard>
    );
  }

  renderShadingDevices(deviceType, stateKey) {
    const { t } = this.props;
    const getProps = (dev, type) => {
      let props = {
        name: dev.name,
        origin: dev.origin,
        size: dev.size,
      };

      if (type === 'front' || type === 'top') {
        props = {
          ...props,
          angle: dev.xAngle,
        };
      }
      return props;
    };

    return (
      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
        {this.state[stateKey].map((dev, index) => (
          <InputFormCard
            title={dev.name}
            key={dev.name}
            onDelete={() => this.removeDevice(stateKey, index)}
            onShowHide={isShow => this.showHideDevice(stateKey, index, isShow)}
            t={t}
          >
            <RectangularInputForm
              {...getProps(dev, deviceType)}
              onDataChanged={(name, data) => this.changeDevice(stateKey, name, data)}
              onFocus={() => this.focusDevice(dev.name)}
              onBlur={() => this.focusDevice(null)}
              t={t}
            />
          </InputFormCard>
        ))}
        {
          <Button
            style={{ width: 150, minHeight: 150 }}
            onClick={() => this.addDevice(deviceType, stateKey)}
          >
            <Glyphicon glyph="glyphicon glyphicon-plus" />
          </Button>
        }
      </div>
    );
  }

  render() {
    const { t } = this.props;
    return (
      <Tabs id="sc-control-panel">
        <Tab eventKey={1} title={t('window')}>
          {this.renderWindow()}
        </Tab>
        <Tab eventKey={2} title={t('left shading device')}>
          {this.renderShadingDevices('left', 'leftDevices')}
        </Tab>
        <Tab eventKey={3} title={t('right shading device')}>
          {this.renderShadingDevices('right', 'rightDevices')}
        </Tab>
        <Tab eventKey={4} title={t('top shading device')}>
          {this.renderShadingDevices('top', 'topDevices')}
        </Tab>
        <Tab eventKey={5} title={t('front shading device')}>
          {this.renderShadingDevices('front', 'frontDevices')}
        </Tab>
      </Tabs>
    );
  }
}

SCControlPanel.defaultProps = {
  window: {},
  shadingDevices: {},
  onDataChanged: null,
};

export default translate('translations')(SCControlPanel);



// WEBPACK FOOTER //
// ./src/components/sc_editor/SCControlPanel.js